import { useNavigate } from 'react-router';
import { useContextSelector as useCS } from 'use-context-selector';
import { Popover, useMediaQuery } from '@mui/material';
import {
  BellIcon,
  ChatIcon,
  LogoIcon,
  FavoriteHeaderIcon,
  LogOutIcon,
  CloseIcon,
  NotificationInfoIcon,
  NotificationCompleteProfile,
  NotificationDenied,
  NotificationApproved,
  NotificationNewRequest,
} from 'assets';
import {
  GlobalConfigContext,
  TGlobalConfigContextState,
} from 'domain/globalConfig/GlobalConfigContext';
import { globalConfigSelectors } from 'domain/globalConfig/selectors';
import { RolesEnum } from 'infrastructure/enums/RolesEnum';
import { Button, Spinner } from 'view/components/index';
import { theme } from 'view/themes/theme';
import { useLocation } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useUserLogOut } from 'view/pages/auth/hooks/useUserLogOut';
import { NoResultsBlock } from 'view/pages/candidates/components';
import {
  PROFILE_NOTIFICATIONS_PER_PAGE,
  useGetProfileNotifications,
} from 'view/pages/profile/hooks/useGetProfileNotifications';

import { TUpdate } from 'domain/types';
import { useUpdateApplicationStatus } from 'view/pages/candidates/hooks/useUpdateApplicationStatus';
import { useUpdateNotificationBulkType } from 'view/pages/profile/hooks/useUpdateNotificationBulkType';
import * as S from './styled';
// eslint-disable-next-line no-restricted-imports
import { useChatSocketContext } from '../../../App';

const NotificationsContainer = ({
  handleNotificationsClose,
  isProfileNotificationsLoading,
  profileNotificationsData,
  roleName,
  getProfileNotifications,
}) => {
  const navigate = useNavigate();
  const globalUpdate: TUpdate<TGlobalConfigContextState> | undefined = useCS(
    GlobalConfigContext,
    globalConfigSelectors.update
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const isLoadingMore = useRef(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 50;

      if (
        isNearBottom &&
        !isLoadingMore.current &&
        profileNotificationsData?.data?.length < profileNotificationsData?.total
      ) {
        isLoadingMore.current = true;

        // Корректно вычисляем следующую страницу
        const loadedItemsCount = profileNotificationsData.data.length;
        const itemsPerPage = PROFILE_NOTIFICATIONS_PER_PAGE;
        const nextPage = Math.floor(loadedItemsCount / itemsPerPage) + 1;

        getProfileNotifications(nextPage, PROFILE_NOTIFICATIONS_PER_PAGE, () => {
          isLoadingMore.current = false;
        });
      }
    };

    container.addEventListener('scroll', handleScroll);
    // eslint-disable-next-line consistent-return
    return () => {
      return container.removeEventListener('scroll', handleScroll);
    };
  }, [profileNotificationsData, getProfileNotifications]);

  const { updateApplicationStatus } =
    useUpdateApplicationStatus(); /* isLoading: isUpdateApplicationStatusLoading, asd */
  const { updateNotificationBulkType } = useUpdateNotificationBulkType();

  const onMakeReadNotificationStatus = (notification: any) => {
    if (notification?.notificationType === 'incompleteProfile') return;
    updateNotificationBulkType(
      {
        bulkType: 'read',
        userId:
          roleName === RolesEnum.JOB_SEEKER ? notification?.applicantId : notification?.receiverId,
        ids: [notification?.id],
      },
      () => {
        if (globalUpdate) {
          globalUpdate({
            profileNotificationsData: {
              ...profileNotificationsData,
              data: profileNotificationsData?.data?.map((item) => {
                return item?.id === notification?.id
                  ? {
                      ...item,
                      isRead: true,
                    }
                  : item;
              }),
            },
          });
        }
      }
    );
  };

  const onDeleteNotification = (notification: any, successFunction: any) => {
    updateNotificationBulkType(
      {
        bulkType: 'remove',
        userId:
          roleName === RolesEnum.JOB_SEEKER ? notification?.applicantId : notification?.receiverId,
        ids: [notification?.id],
      },
      successFunction
    );
  };

  const getNotificationItemIcon = (notificationType: string) => {
    switch (notificationType) {
      case 'newApplication':
        return <NotificationInfoIcon style={{ flexShrink: 0 }} />;
      case 'incompleteProfile':
        return <NotificationCompleteProfile style={{ flexShrink: 0 }} />;
      case 'applicationRejected':
        return <NotificationDenied style={{ flexShrink: 0 }} />;
      case 'applicationApproved':
        return <NotificationApproved style={{ flexShrink: 0 }} />;
      case 'newInvitation':
        return <NotificationNewRequest style={{ flexShrink: 0 }} />;
      default:
        return null;
    }
  };

  /*  const getNotificationItemTitle = (notificationType: string) => {
    switch (notificationType) {
      case 'newApplication':
        return 'New Application';
      case 'completeProfile':
        return 'Complete Profile';
      case 'applicationRejected':
        return 'Your apply denied';
      case 'applicationApproved':
        return 'Your apply approved';
      case 'newInvitation':
        return 'New Job Request';
      default:
        return null;
    }
  }; */

  const getNotificationItemLink = (notification: any) => {
    switch (notification?.notificationType) {
      case 'newApplication':
        return (
          <S.NotificationsMainItemLink
            onClick={(event) => {
              event.stopPropagation();
              updateApplicationStatus(
                {
                  statusForCandidate: 'Applied',
                  statusForRecruiter: 'Pending',
                },
                notification?.applicationId,
                () => {
                  if (!notification.isRead) onMakeReadNotificationStatus(notification);
                  onDeleteNotification(notification, () => {
                    navigate(
                      `/candidates/candidate-profile?id=${notification.applicantId}&jobPostId=${notification.jobPostId}`
                    );
                    handleNotificationsClose();
                  });
                }
              );
            }}
          >
            Check CV
          </S.NotificationsMainItemLink>
        );
      case 'incompleteProfile':
        return (
          <S.NotificationsMainItemLink
            onClick={(event) => {
              event.stopPropagation();
              if (!notification.isRead) onMakeReadNotificationStatus(notification);
              navigate('/profile/work-preferences');
              handleNotificationsClose();
            }}
          >
            Edit Profile
          </S.NotificationsMainItemLink>
        );
      case 'applicationRejected':
        return (
          <S.NotificationsMainItemLink
            onClick={(event) => {
              event.stopPropagation();
              if (!notification.isRead) onMakeReadNotificationStatus(notification);
              navigate('/profile/job-list');
              handleNotificationsClose();
            }}
          >
            Discover Job Posts
          </S.NotificationsMainItemLink>
        );
      case 'applicationApproved':
        return (
          <S.NotificationsMainItemLink
            onClick={(event) => {
              event.stopPropagation();
              if (!notification.isRead) onMakeReadNotificationStatus(notification);
              if (notification?.chatId)
                navigate(`/messenger/chats?conversationId=${notification?.chatId}`);
              handleNotificationsClose();
            }}
          >
            Go to Chat
          </S.NotificationsMainItemLink>
        );
      case 'newInvitation':
        return (
          <S.NotificationsMainItemLink
            onClick={(event) => {
              event.stopPropagation();
              if (!notification.isRead) onMakeReadNotificationStatus(notification);
              navigate(`/profile/view-job-post?id=${notification?.jobPostId}`);
              handleNotificationsClose();
            }}
          >
            Got it
          </S.NotificationsMainItemLink>
        );
      default:
        return null;
    }
  };

  return (
    <S.NotificationsContainer>
      <S.NotificationsHeader>
        <p>
          Notifications <span>({profileNotificationsData?.total || 0})</span>
        </p>
        <div onClick={handleNotificationsClose}>
          <CloseIcon />
        </div>
      </S.NotificationsHeader>
      {!isProfileNotificationsLoading &&
      !profileNotificationsData?.total &&
      !profileNotificationsData?.data?.length ? (
        <S.NotificationsNoContentMain>
          <NoResultsBlock
            title="You're all caught up."
            subTitle={'We will let you know when something new \n comes in.'}
          />
        </S.NotificationsNoContentMain>
      ) : (
        <S.NotificationsMain ref={containerRef}>
          {profileNotificationsData?.data?.map((notification) => {
            return (
              <S.NotificationsMainItem
                key={notification?.id}
                onClick={() => {
                  if (notification.isRead) return;
                  onMakeReadNotificationStatus(notification);
                }}
              >
                {getNotificationItemIcon(notification?.notificationType)}
                <div>
                  <S.NotificationsMainItemTitle>{notification?.title}</S.NotificationsMainItemTitle>
                  <S.NotificationsMainItemDesc>{notification?.content}</S.NotificationsMainItemDesc>
                  {getNotificationItemLink(notification)}
                  {notification?.isRead ? null : <S.NotificationsMainItemIsRead />}
                </div>
              </S.NotificationsMainItem>
            );
          })}
        </S.NotificationsMain>
      )}
      {isProfileNotificationsLoading && (
        <Spinner
          variant="outlined"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />
      )}
    </S.NotificationsContainer>
  );
};

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const matches = useMediaQuery(theme.breakpoints.down('tablet'));
  const globalUpdate: TUpdate<TGlobalConfigContextState> | undefined = useCS(
    GlobalConfigContext,
    globalConfigSelectors.update
  );

  const unreadNotificationsCount: number = useCS(
    GlobalConfigContext,
    globalConfigSelectors.unreadNotificationsCount
  );
  const profileNotificationsData: any = useCS(
    GlobalConfigContext,
    globalConfigSelectors.profileNotificationsData
  );
  const { role, photo, recruiterProfile, jobSeekerProfile } = useCS(
    GlobalConfigContext,
    globalConfigSelectors.userData
  );

  /* const userData = useCS(GlobalConfigContext, globalConfigSelectors.userData); */

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [renderDate, setRenderDate] = useState<any>(null);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState<HTMLButtonElement | null>(
    null
  );

  const { isLoading: isProfileNotificationsLoading, getProfileNotifications } =
    useGetProfileNotifications();
  const { send } = useUserLogOut();
  const { subscribeToEvents, unsubscribeToEvents, isSocketConnected } = useChatSocketContext();

  const hasMessages = (messageCounts) => {
    if (
      !messageCounts ||
      typeof messageCounts !== 'object' ||
      Object.keys(messageCounts).length === 0
    ) {
      return false;
    }
    return Object.values(messageCounts).some((count: any) => {
      return count > 0;
    });
  };

  useEffect(() => {
    if (!isSocketConnected) return;

    subscribeToEvents('user', (data) => {
      if (data?.type === 'unreads' && data?.payload) {
        setRenderDate(new Date());
        const unreadMess = hasMessages(data?.payload);
        sessionStorage.setItem('hasUnreadMess', JSON.stringify(unreadMess));
      }
    });

    // eslint-disable-next-line consistent-return
    return () => {
      unsubscribeToEvents('user');
    };
  }, [isSocketConnected]);

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleNotificationsClick = (event: any) => {
    if (globalUpdate)
      globalUpdate({
        profileNotificationsData: {
          data: [],
          limit: PROFILE_NOTIFICATIONS_PER_PAGE,
          page: 1,
          total: 0,
        },
      });
    setNotificationsAnchorEl(event.currentTarget);
    getProfileNotifications();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchorEl(null);
    if (globalUpdate)
      globalUpdate({
        profileNotificationsData: {
          data: [],
          limit: PROFILE_NOTIFICATIONS_PER_PAGE,
          page: 1,
          total: 0,
        },
      });
  };

  const open = Boolean(anchorEl);
  const notificationsOpen = Boolean(notificationsAnchorEl);
  const id = open ? 'simple-popover' : undefined;
  const notificationsId = notificationsOpen ? 'simple-popover-2' : undefined;

  const isFavoritePage = location.pathname === '/candidates/favorites';
  const isChatsPage = location.pathname === '/messenger/chats';
  const el: any = sessionStorage.getItem('hasUnreadMess');

  const hasUnreadMess = useMemo(() => {
    if (el) return JSON.parse(el);
    return false;
  }, [el, renderDate]);

  return (
    <S.HeaderWrapper>
      <S.HeaderContent>
        {!matches ? (
          <S.HeaderLeftBlock>
            <LogoIcon />
            <S.HeaderLink
              selected={
                location.pathname === '/candidates/candidates-list' ||
                location.pathname === '/profile/job-list'
              }
              onClick={() => {
                if (role?.name === RolesEnum.JOB_SEEKER) return navigate('/profile/job-list');
                return navigate('/candidates/candidates-list');
              }}
            >
              {role?.name === RolesEnum.JOB_SEEKER ? 'Job Posts' : 'Candidates'}
            </S.HeaderLink>
            <S.HeaderLink
              selected={
                location.pathname === '/candidates/my-job-posts' ||
                location.pathname === '/profile/applications'
              }
              onClick={() => {
                if (role?.name === RolesEnum.JOB_SEEKER)
                  return navigate('/profile/applications?applicantType=all');
                return navigate('/candidates/my-job-posts?postStatus=active', { replace: true });
              }}
            >
              {role?.name === RolesEnum.JOB_SEEKER ? 'My Applications' : 'My Job Posts'}
            </S.HeaderLink>
            <S.HeaderLink
              selected={location.pathname.startsWith('/companies')}
              onClick={() => {
                navigate('/companies');
              }}
            >
              Companies
            </S.HeaderLink>
            {/* <S.HeaderLink selected={false}>{role?.name}</S.HeaderLink> */}
          </S.HeaderLeftBlock>
        ) : (
          <S.HeaderLeftBlock>
            <LogoIcon />
          </S.HeaderLeftBlock>
        )}
        <S.HeaderRightBlock>
          <div
            style={{
              display: 'flex',
              gap: '2px',
            }}
          >
            {role?.name === RolesEnum.JOB_SEEKER ? null : (
              <FavoriteHeaderIcon
                style={{
                  fill: isFavoritePage ? '#663497' : '#fff',
                  cursor: isFavoritePage ? 'not-allowed' : 'pointer',
                  pointerEvents: isFavoritePage ? 'not-allowed' : 'pointer',
                }}
                onClick={() => {
                  navigate('/candidates/favorites', { replace: true });
                }}
              />
            )}
            <S.HeaderMessages
              onClick={() => {
                navigate('/messenger/chats', { replace: false });
              }}
            >
              {hasUnreadMess ? <span /> : null}
              <ChatIcon
                style={{
                  fill: isChatsPage ? '#663497' : '#fff',
                  cursor: isChatsPage ? 'not-allowed' : 'pointer',
                  pointerEvents: isChatsPage ? 'not-allowed' : 'pointer',
                }}
              />
            </S.HeaderMessages>

            <S.HeaderNotifications
              aria-describedby={notificationsId}
              onClick={handleNotificationsClick}
            >
              {unreadNotificationsCount ? <span /> : null}
              <BellIcon />
            </S.HeaderNotifications>
            <Popover
              id={notificationsId}
              open={notificationsOpen}
              anchorEl={notificationsAnchorEl}
              onClose={handleNotificationsClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              disableScrollLock
              sx={{ '.MuiPaper-root': { borderRadius: '12px!important' } }}
            >
              <NotificationsContainer
                handleNotificationsClose={handleNotificationsClose}
                isProfileNotificationsLoading={isProfileNotificationsLoading}
                profileNotificationsData={profileNotificationsData}
                roleName={role?.name}
                getProfileNotifications={getProfileNotifications}
              />
            </Popover>
          </div>
          {!matches ? (
            <Button
              variant="outlined"
              text={role?.name === RolesEnum.JOB_SEEKER ? 'Edit Profile' : 'Create Job Post'}
              className="header-button"
              onClick={() => {
                return role?.name === RolesEnum.RECRUITER
                  ? navigate('/create-job-post/setup')
                  : navigate('/profile/work-preferences');
              }}
            />
          ) : null}
          {photo?.link ? (
            <>
              <S.HeaderAvatar aria-describedby={id} onClick={handleClick}>
                <S.HeaderAvatarImg src={photo.link} alt={photo.name} />
              </S.HeaderAvatar>
              <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                disableScrollLock
                sx={{ '.MuiPaper-root': { borderRadius: '12px!important' } }}
              >
                <S.HeaderPopoverContent>
                  <S.HeaderPopoverContentHeading>
                    <div>
                      <img
                        src={photo.link}
                        alt={photo.name}
                        style={{ width: '56px', height: '56px' }}
                      />
                      <S.HeaderPopoverContentHeadingTexts>
                        <S.HeaderPopoverContentHeadingTextsTitle>
                          {recruiterProfile?.username || jobSeekerProfile?.username}
                        </S.HeaderPopoverContentHeadingTextsTitle>
                        <S.HeaderPopoverContentHeadingTextsDesc>
                          {role?.name === RolesEnum.JOB_SEEKER ? 'Job Seeker' : 'Recruiter'}
                        </S.HeaderPopoverContentHeadingTextsDesc>
                      </S.HeaderPopoverContentHeadingTexts>
                    </div>
                    <Button
                      variant="outlined"
                      text="View Profile"
                      className={matches ? 'header-button' : ''}
                      style={{ width: '100%' }}
                      onClick={() => {
                        if (role?.name === RolesEnum.JOB_SEEKER) {
                          navigate(`/candidates/candidate-profile?id=${jobSeekerProfile?.id}`);
                        } else {
                          navigate('/profile/profile-settings');
                        }
                        handleClose();
                      }}
                      disabled={jobSeekerProfile && !jobSeekerProfile?.hasWorkPreferencesFilled}
                    />
                    {matches ? (
                      <Button
                        variant="outlined"
                        text={
                          role?.name === RolesEnum.JOB_SEEKER ? 'Edit Profile' : 'Create Job Post'
                        }
                        className="header-button"
                        style={{ width: '100%' }}
                        onClick={() => {
                          return role?.name === RolesEnum.RECRUITER
                            ? navigate('/create-job-post/setup')
                            : navigate('/profile/work-preferences');
                        }}
                      />
                    ) : null}
                  </S.HeaderPopoverContentHeading>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '3px',
                      padding: '16px 0',
                      borderBottom: '1px solid #D8DBE8',
                    }}
                  >
                    {matches ? (
                      <>
                        <S.MenuItemLink
                          selected={
                            location.pathname === '/candidates/candidates-list' ||
                            location.pathname === '/profile/job-list'
                          }
                          onClick={() => {
                            if (role?.name === RolesEnum.JOB_SEEKER)
                              return navigate('/profile/job-list');
                            return navigate('/candidates/candidates-list');
                          }}
                        >
                          {role?.name === RolesEnum.JOB_SEEKER ? 'Job Posts' : 'Candidates'}
                        </S.MenuItemLink>
                        <S.MenuItemLink
                          selected={
                            location.pathname === '/candidates/my-job-posts' ||
                            location.pathname === '/profile/applications'
                          }
                          onClick={() => {
                            if (role?.name === RolesEnum.JOB_SEEKER)
                              return navigate('/profile/applications?applicantType=all');
                            return navigate('/candidates/my-job-posts?postStatus=active', {
                              replace: true,
                            });
                          }}
                        >
                          {role?.name === RolesEnum.JOB_SEEKER ? 'My Applications' : 'My Job Posts'}
                        </S.MenuItemLink>
                        <S.MenuItemLink
                          selected={location.pathname.startsWith('/companies')}
                          onClick={() => {
                            return navigate('/companies');
                          }}
                        >
                          Companies
                        </S.MenuItemLink>
                      </>
                    ) : null}
                    <S.MenuItemLink
                      onClick={() => {
                        navigate('/profile/account-settings');
                        handleClose();
                      }}
                      selected={location.pathname === '/profile/account-settings'}
                    >
                      Account Settings
                    </S.MenuItemLink>
                    <S.MenuItemLink
                      onClick={() => {
                        navigate('/profile/help-center');
                        handleClose();
                      }}
                      selected={location.pathname === '/profile/help-center'}
                    >
                      Help Center
                    </S.MenuItemLink>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '3px',
                      padding: '16px 0 0 0',
                    }}
                  >
                    <S.MenuItemLink
                      onClick={() => {
                        return send();
                      }}
                    >
                      Sign Out <LogOutIcon />
                    </S.MenuItemLink>
                  </div>
                </S.HeaderPopoverContent>
              </Popover>
            </>
          ) : (
            <S.HeaderAvatar />
          )}
        </S.HeaderRightBlock>
      </S.HeaderContent>
    </S.HeaderWrapper>
  );
};

export default Header;
